import { shouldUseHashRouter } from "./globalUtils.js";

// Punto de entrada del SSO de la UNED. Acepta el destino de vuelta en `URL=`,
// que su formulario de login conserva en el campo oculto `urlrebote`.
const UNED_SSO_ENTRY = "https://sso.uned.es/sso/index.aspx";

const RETURN_PARAM = "unedReturn";
const GENRE_PARAM = "unedGenre";

// Solo aceptamos rutas internas absolutas de raíz: ni "//host", ni un esquema
// ("javascript:", "https:"), ni la barra invertida que algunos navegadores
// normalizan a "/". Evita convertir el retorno del SSO en un open redirect.
function isSafeInternalPath(path) {
  return (
    typeof path === "string" &&
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.includes(":") &&
    !path.includes("\\")
  );
}

/**
 * ¿Corre la web dentro de un navegador embebido (la app, u otra app)?
 *
 * Decide cómo se abre el login de la UNED: en un navegador real, en pestaña
 * nueva (la pestaña de rewrite.es sigue viva; desde el campus de la UNED el
 * botón Atrás es un bucle salida → login → campus). En uno embebido, en la
 * misma ventana: allí una ventana nueva se traga el toque en silencio y, aunque
 * abriera, no compartiría la cookie de sesión.
 *
 * Señales, por orden de fiabilidad: la marca `RewriteApp/` que pone la app,
 * el token `wv` del WebView de Android (documentado por Google) y un iOS sin
 * el token `Safari/`, que es como se presenta WKWebView. Un falso positivo en
 * otra app (Instagram, Gmail…) es deseable: ahí tampoco funcionan las pestañas.
 */
export function isEmbeddedUserAgent(userAgent) {
  const ua = String(userAgent || "");
  if (/RewriteApp\//.test(ua)) return true;
  if (/\bwv\b/.test(ua)) return true;
  return /iPhone|iPad|iPod/.test(ua) && !/Safari\//.test(ua);
}

export const isEmbeddedBrowser = () =>
  typeof navigator !== "undefined" && isEmbeddedUserAgent(navigator.userAgent);

/**
 * ¿Este navegador bloquea las cookies de terceros, y por tanto el iframe del
 * corrector nunca verá la sesión de la UNED?
 *
 * El corrector es un iframe de `qinnova2.uned.es` dentro de rewrite.es, o sea
 * contexto de terceros, y la sesión es la cookie `usuarioUNEDv2` en `.uned.es`.
 * WebKit bloquea esas cookies: comprobado el 22-09-2026 en Safari de escritorio
 * y en la app iOS, donde el usuario se autentica bien y aun así el widget nunca
 * arranca. Cargado a pelo (top-level) el mismo corrector funciona y corrige.
 *
 * Quién bloquea: Safari de escritorio, y **todos** los navegadores de iOS/iPadOS
 * —Apple obliga a usar WKWebView, así que Chrome o Firefox allí bloquean igual—.
 * Quién no: Chrome, Edge, Opera y Firefox de escritorio, Chrome en Android y,
 * importante, **la app RE-Write en Android**, donde el iframe funciona hoy y no
 * queremos cambiar nada.
 *
 * Los dos detalles que hacen falta para no colarse:
 * - Chrome y Edge llevan `Safari/` en su UA por compatibilidad, así que hay que
 *   descartarlos explícitamente antes de mirar ese token.
 * - iPadOS 13+ se anuncia como `Macintosh`. Se distingue de un Mac de verdad
 *   porque expone varios puntos de contacto táctiles.
 *
 * Se reciben `userAgent` y `maxTouchPoints` como argumentos (en vez de leer
 * `navigator`) para poder probar la función con UAs reales.
 */
export function blocksThirdPartyCookies(userAgent, maxTouchPoints = 0) {
  const ua = String(userAgent || "");

  // El WebView de Android se anuncia con `wv` y también lleva `Safari/`: se
  // descarta aquí para que la app en Android nunca entre por la rama de WebKit.
  if (/\bwv\b/.test(ua) || /\bAndroid\b/.test(ua)) return false;

  // Cualquier navegador en un dispositivo iOS es WKWebView por obligación.
  if (/iPhone|iPad|iPod/.test(ua)) return true;

  // iPadOS 13+ disfrazado de escritorio.
  if (/Macintosh/.test(ua) && Number(maxTouchPoints) > 1) return true;

  // Safari de escritorio: tiene `Safari/` y ninguna marca de los que lo imitan.
  if (/\b(Chrome|Chromium|Edg|EdgiOS|OPR|Firefox)\b/.test(ua)) return false;
  return /Safari\//.test(ua);
}

export const blocksThirdPartyCookiesHere = () =>
  typeof navigator !== "undefined" &&
  blocksThirdPartyCookies(navigator.userAgent, navigator.maxTouchPoints);

// Primera versión de Android que trae la raíz HARICA TLS RSA Root CA 2021 en su
// almacén. Comprobado en AOSP: `system/ca-certificates/files/b92fd57f.0` existe
// en `android14-release` y no en `android13-release`.
export const ANDROID_WITH_HARICA = 14;

// Primera versión de RE-Write que añade esa raíz como ancla de confianza propia
// para `uned.es` (ver `plugins/withUnedTrustAnchor.js` en el repo de la app).
export const APP_WITH_UNED_TRUST_ANCHOR = [1, 0, 4];

/**
 * Qué cliente está leyendo la página, a partir del User-Agent.
 *
 * La app marca su UA con `RewriteApp/<versión>` **desde la 1.0.3**, así que un
 * `appVersion` nulo dentro de un navegador embebido significa «1.0.2 o anterior,
 * u otra app». El UA de Android trae además su versión mayor.
 */
export function readClientInfo(userAgent) {
  const ua = String(userAgent || "");
  const app = /RewriteApp\/(\d+)\.(\d+)\.(\d+)/.exec(ua);
  const android = /Android\s+(\d+)/.exec(ua);
  return {
    embedded: isEmbeddedUserAgent(ua),
    appVersion: app ? [Number(app[1]), Number(app[2]), Number(app[3])] : null,
    androidMajor: android ? Number(android[1]) : null,
  };
}

function isOlderThan(version, reference) {
  for (let i = 0; i < reference.length; i += 1) {
    const part = version[i] ?? 0;
    if (part !== reference[i]) return part < reference[i];
  }
  return false;
}

/**
 * ¿Este cliente es incapaz de validar el certificado de la UNED?
 *
 * Los dominios de la UNED encadenan con la raíz HARICA 2021 y su servidor no
 * envía cadena cruzada, así que en Android anterior al 14 solo la reconocen las
 * versiones de la app que la llevan empaquetada. En ese caso el WebView cancela
 * la navegación sin emitir ningún evento y el botón de acceso no hace nada.
 */
export function lacksUnedTrustAnchor(info) {
  const { embedded, appVersion, androidMajor } = info || {};
  if (!embedded) return false;
  if (androidMajor === null || androidMajor === undefined) return false;
  if (androidMajor >= ANDROID_WITH_HARICA) return false;
  // Sin marca en el UA no se puede afinar más: es una app anterior a la 1.0.3,
  // que tampoco lleva el ancla. Quien lo use avisa en condicional.
  if (!appVersion) return true;
  return isOlderThan(appVersion, APP_WITH_UNED_TRUST_ANCHOR);
}

// Raíz del SSO: responde un 301 minúsculo y comparte certificado con todo
// `*.uned.es`, así que sirve de sonda para el dominio entero.
export const UNED_PROBE_URL = "https://sso.uned.es/";
export const UNED_PROBE_TIMEOUT_MS = 4000;

/**
 * ¿Se alcanza la UNED desde este cliente?
 *
 * `no-cors` devuelve una respuesta opaca: no se lee nada de ella, solo importa
 * si la promesa resuelve. Un certificado que no valida —el fallo real en
 * Android ≤13— la hace rechazar, y ese es justamente el dato que buscamos.
 *
 * Devuelve `true` si responde, `false` si el intento falla y `null` si se agota
 * el tiempo, porque una red lenta no es prueba de nada.
 */
export function probeUnedReachable({
  url = UNED_PROBE_URL,
  timeoutMs = UNED_PROBE_TIMEOUT_MS,
  fetchImpl = typeof fetch === "function" ? fetch : null,
} = {}) {
  if (!fetchImpl) return Promise.resolve(null);

  const controller =
    typeof AbortController === "function" ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), timeoutMs);

  return fetchImpl(url, {
    mode: "no-cors",
    cache: "no-store",
    credentials: "omit",
    signal: controller?.signal,
  })
    .then(() => true)
    .catch((error) => (error && error.name === "AbortError" ? null : false))
    .finally(() => clearTimeout(timer));
}

/**
 * URL del login de la UNED que, al terminar, devuelve al usuario a rewrite.es.
 *
 * El destino se codifica con parámetros planos y hermanos (`unedReturn`,
 * `unedGenre`) y SIN fragmento: el `#` es frágil dentro de una cabecera
 * `Location` y el hash del router lo reconstruye `consumeUnedReturn()` en
 * cliente. Al ir planos, el valor sobrevive tanto si el SSO decodifica el
 * parámetro `URL=` una vez como si lo hace dos.
 *
 * Se usa `window.location.origin` en vez de rewrite.es hardcodeado para poder
 * probar el ciclo completo contra un build local.
 */
export function buildUnedAuthUrl({ returnPath, genreId } = {}) {
  const returnUrl = new URL("/", window.location.origin);

  if (isSafeInternalPath(returnPath)) {
    returnUrl.searchParams.set(RETURN_PARAM, returnPath);
    if (genreId) {
      returnUrl.searchParams.set(GENRE_PARAM, String(genreId));
    }
  }

  return `${UNED_SSO_ENTRY}?URL=${encodeURIComponent(returnUrl.toString())}`;
}

/**
 * Al volver del SSO, traduce `?unedReturn=/ruta&unedGenre=1` a la ubicación que
 * espera el router y limpia la barra de direcciones.
 *
 * Debe ejecutarse ANTES de montar React para que el router lea ya la ubicación
 * correcta en su primer render (nada de aterrizar en "/" y navegar después).
 * Se descartan los demás parámetros de la query: en este retorno no hay ninguno
 * propio y el SSO puede añadir los suyos.
 */
export function consumeUnedReturn() {
  if (typeof window === "undefined") {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  if (!params.has(RETURN_PARAM)) {
    return;
  }

  const returnPath = params.get(RETURN_PARAM);
  const genreId = params.get(GENRE_PARAM);

  const route = isSafeInternalPath(returnPath) ? returnPath : "/";
  const query = genreId ? `?genre=${encodeURIComponent(genreId)}` : "";

  const nextUrl = shouldUseHashRouter()
    ? `${window.location.pathname}#${route}${query}`
    : `${route}${query}`;

  window.history.replaceState(null, "", nextUrl);
}
